import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { notifications } from '@mantine/notifications';
import { Modal, Button, ControlledInput } from '@shared/ui';
import { useUpdateAssemblyType, useCreateAssemblyType, type AssemblyType } from '@modules/products';

const assemblyTypeSchema = yup.object({
  name: yup.string().trim().required('Название обязательно'),
});

type AssemblyTypeForm = yup.InferType<typeof assemblyTypeSchema>;

interface EditAssemblyTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  assemblyType: AssemblyType | null;
  onSuccess?: () => void;
}

export default function EditAssemblyTypeModal({ isOpen, onClose, assemblyType, onSuccess }: EditAssemblyTypeModalProps) {
  const updateAssemblyType = useUpdateAssemblyType();
  const createAssemblyType = useCreateAssemblyType();
  const isEdit = !!assemblyType;
  const { control, handleSubmit, reset, formState: { errors } } = useForm<AssemblyTypeForm>({
    resolver: yupResolver(assemblyTypeSchema),
    defaultValues: { name: '' },
  });

  useEffect(() => {
    reset({ name: assemblyType?.name ?? '' });
  }, [assemblyType, reset]);

  const onSubmit = async (data: AssemblyTypeForm) => {
    try {
      if (isEdit && assemblyType) {
        await updateAssemblyType.mutateAsync({
          id: assemblyType.id,
          data: { name: data.name },
        });
      } else {
        await createAssemblyType.mutateAsync({ name: data.name });
      }

      notifications.show({
        title: 'Успешно',
        message: `Тип сборки успешно ${isEdit ? 'обновлен' : 'создан'}`,
        color: 'green',
      });

      onSuccess?.();
      onClose();
    } catch (error) {
      notifications.show({
        title: 'Ошибка',
        message: `Ошибка при ${isEdit ? 'обновлении' : 'создании'} типа сборки`,
        color: 'red',
      });
    }
  };

  const handleClose = () => {
    reset({ name: '' });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} size={400} onClose={handleClose} title={isEdit ? "Редактировать тип сборки" : "Добавить тип сборки"}>
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <ControlledInput
            id="assembly-type-name"
            label="Название"
            name="name"
            control={control}
            error={errors.name}
          />
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
            <Button type="button" onClick={handleClose} variant='secondary'>
              Отмена
            </Button>
            <Button type="submit" variant="primary" loading={updateAssemblyType.isPending || createAssemblyType.isPending}>
              Сохранить
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}

